import {
	ChangeDetectionStrategy,
	Component,
	DestroyRef,
	LOCALE_ID,
	computed,
	effect,
	inject,
	input,
	signal,
} from '@angular/core';
import { CommonModule, formatDate } from '@angular/common';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { catchError, EMPTY, finalize } from 'rxjs';
import { DateAdapter } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { PolishDateAdapter } from '@/common/polish-date-adapter/polish-date-adapter';
import { InfoboxService } from '@/common/infobox/infobox.service';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatChipsModule } from '@angular/material/chips';
import { MatDividerModule } from '@angular/material/divider';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import {
	DriverEnovaMapping,
	DriverIntegration,
	GratyfikantMapping,
	Organization,
} from './_models/drivers-integration.model';
import { DriversIntegrationApiService, EnovaPayload } from './_api/drivers-integration.api.service';

@Component({
	selector: 'app-drivers-integration',
	standalone: true,
	imports: [
		CommonModule,
		ReactiveFormsModule,
		MatButtonModule,
		MatCardModule,
		MatChipsModule,
		MatDatepickerModule,
		MatDividerModule,
		MatFormFieldModule,
		MatInputModule,
		MatProgressSpinnerModule,
		MatSelectModule,
		MatTableModule,
		MatTooltipModule,
	],
	templateUrl: './drivers-integration.component.html',
	styleUrl: './drivers-integration.component.scss',
	changeDetection: ChangeDetectionStrategy.OnPush,
	providers: [
		{ provide: LOCALE_ID, useValue: 'pl-PL' },
		{ provide: DateAdapter, useClass: PolishDateAdapter },
	],
})
export class DriversIntegrationComponent {
	readonly driverId = input.required<string>();

	readonly organizations = signal<Organization[]>([]);
	readonly gratyfikantMappings = signal<GratyfikantMapping[]>([]);
	readonly enovaMappings = signal<DriverEnovaMapping[]>([]);

	readonly isLoadingIntegration = signal(false);
	readonly isSavingIntegration = signal(false);
	readonly isLoadingGratyfikant = signal(false);
	readonly isSavingGratyfikant = signal(false);
	readonly isLoadingEnova = signal(false);
	readonly isSavingEnova = signal(false);

	readonly showForm = signal(false);
	readonly editingId = signal<string | null>(null);
	readonly showEnovaForm = signal(false);
	readonly editingEnovaId = signal<number | null>(null);

	readonly displayedColumns = ['organization', 'gratyfikant_id', 'date_from', 'date_to', 'actions'];
	readonly enovaColumns = ['enova_id', 'date_from', 'date_to', 'actions'];

	readonly integrationForm = new FormGroup({
		nawigator_id: new FormControl<string | null>(null),
		driver_card: new FormControl<string | null>(null),
	});

	readonly enovaForm = new FormGroup({
		enova_id: new FormControl('', {
			nonNullable: true,
			validators: [Validators.required, Validators.maxLength(255)],
		}),
		date_from: new FormControl<Date | null>(null, Validators.required),
		date_to: new FormControl<Date | null>(null),
	});

	readonly gratyfikantForm = new FormGroup({
		organization_id: new FormControl<number | null>(null, Validators.required),
		gratyfikant_id: new FormControl('', { nonNullable: true, validators: [Validators.required] }),
		date_from: new FormControl('', { nonNullable: true, validators: [Validators.required] }),
		date_to: new FormControl<string | null>(null),
	});

	readonly mappingRows = computed(() => {
		const orgMap = new Map(this.organizations().map((o) => [o.id, o]));
		return this.gratyfikantMappings().map((m) => {
			const org = orgMap.get(m.organization_id);
			return {
				...m,
				organizationLabel: org ? `#${org.id} · ${org.shortName}` : `#${m.organization_id}`,
			};
		});
	});

	private readonly api = inject(DriversIntegrationApiService);
	private readonly destroyRef = inject(DestroyRef);
	private readonly infobox = inject(InfoboxService);

	constructor() {
		effect(
			() => {
				const id = this.driverId();
				if (!id) return;
				this.loadIntegration(id);
				this.loadGratyfikant(id);
				this.loadEnova(id);
			},
			{ allowSignalWrites: true },
		);
		this.loadOrganizations();
	}

	saveIntegration(): void {
		this.isSavingIntegration.set(true);
		const payload: DriverIntegration = {
			nawigator_id: this.integrationForm.controls.nawigator_id.value,
			driver_card: this.integrationForm.controls.driver_card.value,
		};
		this.api
			.saveIntegration(this.driverId(), payload)
			.pipe(
				catchError(() => EMPTY),
				finalize(() => this.isSavingIntegration.set(false)),
				takeUntilDestroyed(this.destroyRef),
			)
			.subscribe(() => {
				this.infobox.success('Zapisano');
			});
	}

	openAddForm(): void {
		this.editingId.set(null);
		this.gratyfikantForm.reset({
			organization_id: null,
			gratyfikant_id: '',
			date_from: '',
			date_to: null,
		});
		this.showForm.set(true);
	}

	openEditForm(mapping: GratyfikantMapping): void {
		this.editingId.set(mapping.id);
		this.gratyfikantForm.setValue({
			organization_id: mapping.organization_id,
			gratyfikant_id: mapping.gratyfikant_id,
			date_from: mapping.date_from,
			date_to: mapping.date_to,
		});
		this.showForm.set(true);
	}

	cancelForm(): void {
		this.showForm.set(false);
		this.editingId.set(null);
	}

	saveGratyfikant(): void {
		if (this.gratyfikantForm.invalid) {
			this.gratyfikantForm.markAllAsTouched();
			return;
		}

		const value = this.gratyfikantForm.getRawValue();
		const payload = {
			organization_id: value.organization_id as number,
			gratyfikant_id: value.gratyfikant_id,
			date_from: value.date_from,
			date_to: value.date_to || null,
		};

		this.isSavingGratyfikant.set(true);
		const driverId = this.driverId();
		const editingId = this.editingId();
		const request$ = editingId
			? this.api.updateGratyfikant(driverId, editingId, payload)
			: this.api.createGratyfikant(driverId, payload);

		request$
			.pipe(
				catchError(() => EMPTY),
				finalize(() => this.isSavingGratyfikant.set(false)),
				takeUntilDestroyed(this.destroyRef),
			)
			.subscribe(() => {
				this.showForm.set(false);
				this.editingId.set(null);
				this.loadGratyfikant(driverId);
				this.infobox.success('Zapisano');
			});
	}

	deleteGratyfikant(id: string): void {
		this.api
			.deleteGratyfikant(this.driverId(), id)
			.pipe(
				catchError(() => EMPTY),
				takeUntilDestroyed(this.destroyRef),
			)
			.subscribe(() => {
				this.gratyfikantMappings.update((rows) => rows.filter((m) => m.id !== id));
			});
	}

	openAddEnovaForm(): void {
		this.editingEnovaId.set(null);
		this.enovaForm.reset({ enova_id: '', date_from: null, date_to: null });
		this.showEnovaForm.set(true);
	}

	openEditEnovaForm(mapping: DriverEnovaMapping): void {
		this.editingEnovaId.set(mapping.id);
		this.enovaForm.setValue({
			enova_id: mapping.enova_id,
			date_from: new Date(`${mapping.date_from}T00:00:00`),
			date_to: mapping.date_to ? new Date(`${mapping.date_to}T00:00:00`) : null,
		});
		this.showEnovaForm.set(true);
	}

	cancelEnovaForm(): void {
		this.showEnovaForm.set(false);
		this.editingEnovaId.set(null);
	}

	saveEnova(): void {
		const value = this.enovaForm.getRawValue();
		if (this.enovaForm.invalid || !value.date_from) {
			this.enovaForm.markAllAsTouched();
			return;
		}

		const payload: EnovaPayload = {
			enova_id: value.enova_id.trim(),
			date_from: formatDate(value.date_from, 'yyyy-MM-dd', 'pl-PL'),
			date_to: value.date_to ? formatDate(value.date_to, 'yyyy-MM-dd', 'pl-PL') : null,
		};
		const driverId = this.driverId();
		const editingId = this.editingEnovaId();
		const request$ =
			editingId === null
				? this.api.createEnova(driverId, payload)
				: this.api.updateEnova(driverId, editingId, payload);

		this.isSavingEnova.set(true);
		request$
			.pipe(
				catchError(() => EMPTY),
				finalize(() => this.isSavingEnova.set(false)),
				takeUntilDestroyed(this.destroyRef),
			)
			.subscribe(() => {
				this.showEnovaForm.set(false);
				this.editingEnovaId.set(null);
				this.loadEnova(driverId);
				this.infobox.success('Zapisano');
			});
	}

	deleteEnova(id: number): void {
		this.api
			.deleteEnova(this.driverId(), id)
			.pipe(
				catchError(() => EMPTY),
				takeUntilDestroyed(this.destroyRef),
			)
			.subscribe(() => {
				this.enovaMappings.update((rows) => rows.filter((m) => m.id !== id));
			});
	}

	private loadIntegration(driverId: string): void {
		this.isLoadingIntegration.set(true);
		this.api
			.getIntegration(driverId)
			.pipe(
				catchError(() => EMPTY),
				finalize(() => this.isLoadingIntegration.set(false)),
				takeUntilDestroyed(this.destroyRef),
			)
			.subscribe(({ data }) => {
				this.integrationForm.setValue({
					nawigator_id: data.nawigator_id,
					driver_card: data.driver_card,
				});
			});
	}

	private loadGratyfikant(driverId: string): void {
		this.isLoadingGratyfikant.set(true);
		this.api
			.getGratyfikant(driverId)
			.pipe(
				catchError(() => EMPTY),
				finalize(() => this.isLoadingGratyfikant.set(false)),
				takeUntilDestroyed(this.destroyRef),
			)
			.subscribe(({ data }) => {
				this.gratyfikantMappings.set(data);
			});
	}

	private loadEnova(driverId: string): void {
		this.isLoadingEnova.set(true);
		this.api
			.getEnova(driverId)
			.pipe(
				catchError(() => EMPTY),
				finalize(() => this.isLoadingEnova.set(false)),
				takeUntilDestroyed(this.destroyRef),
			)
			.subscribe(({ data }) => {
				this.enovaMappings.set(data);
			});
	}

	private loadOrganizations(): void {
		this.api
			.getOrganizations()
			.pipe(
				catchError(() => EMPTY),
				takeUntilDestroyed(this.destroyRef),
			)
			.subscribe((data) => {
				this.organizations.set(data.results);
			});
	}
}
